import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import FormStepper from '@/components/ui/FormStepper/FormStepper';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import { ClassificationDtoIn } from '@/models/in/ClassificationDtoIn';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { SupportedFullInfoDtoIn } from '@/models/in/SupportedFullInfoDtoIn';
import useFundContext from '@/contexts/Fund/useFundContext';
import { supportedFormSchema, defaultFormValues, MIN_ONE_BANK_ACCOUNT, type SupportedFormData } from './schemas/supportedFormSchema';
import Alert from '@/components/ui/Alert';
import SupportedPersonalInfo from './SupportedPersonalInfo';
import FamilyInfo from './FamilyInfo';
import BankAccountInfo from './BankAccountInfo';
import ClassifictionsSection from './ClassifictionsSection';
import DocumentsSection from './DocumentsSection';
import DeclarationsSection from './DeclarationsSection';
import { mapFormDataToDto, mapFormDataToReenterDto } from './utils/mapFormDataToDto';
import { mapExistingSupportedToFormData, existingClassificationIdsOf, standingClassificationNamesOf } from './utils/mapExistingSupportedToFormData';
import type { SupportedFullInfoDtoOut } from '@/models/out/SupportedFullInfoDtoOut';
import { offerTaskSatisfaction } from '@/helpers/surveyStorage';

interface NewSupportedFormProps {
  /**
   * `created` carries what the caller needs to carry on with the supported it has just made
   * (HAT-1453: straight into a support request). `canRequest` is true only when the supported
   * already has an approved classification - the server approves Basic on the spot and sends
   * every other type to the committee, and a request form for a supported with no approved
   * classification has no goals to offer.
   */
  onSuccess: (createdId: number | null, created?: { contact: ContactDtoIn; canRequest: boolean }) => void;
  onError: (message: string) => void;
  initialIsraeliId?: string;
  existingSupported?: SupportedFullInfoDtoIn | null;
  /**
   * A draft written in the Nedarim Plus personal area, already mapped to this form's shape.
   *
   * ⚠️ Only the keys in RESTORABLE_KEYS ever arrive - never documents, classifications or the
   * declarations tick. See mapSupportedDraftToFormData for why each of those is left out.
   */
  initialValues?: Partial<SupportedFormData> | null;
}

const NewSupportedForm = ({ onSuccess, onError, initialIsraeliId, existingSupported, initialValues }: NewSupportedFormProps) => {
  const { t } = useTranslation();
  const methods = useForm<SupportedFormData>({
    resolver: zodResolver(supportedFormSchema) as any,
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });
  const { data: classifications } = useGetAuthApi<ClassificationDtoIn[]>('classifications');
  const { currentFund } = useFundContext();

  /*
   * HAT-1464: a deleted supported being entered again. Their ID is still taken by the deleted record,
   * so the form is sent to that record instead of creating a new one. The lookup only hands a found
   * contact over here when it is a deleted one (CheckExistence).
   */
  const reenteredSupportedId = existingSupported?.contact.isArchived ? existingSupported.contact.id : null;
  const existingClassificationIds = useMemo(() => existingClassificationIdsOf(existingSupported), [existingSupported]);
  const standingClassificationNames = useMemo(() => standingClassificationNamesOf(existingSupported), [existingSupported]);

  const submitUrl = !currentFund?.id
    ? ''
    : reenteredSupportedId
      ? `funds/${currentFund.id}/supporteds/${reenteredSupportedId}/reenter`
      : `funds/${currentFund.id}/supporteds`;
  const { mutate: createSupported, isPending: isSubmitting } = usePostAuthApi<SupportedFullInfoDtoIn, SupportedFullInfoDtoOut>(submitUrl);

  // Watch for changes in selected classifications to determine if FamilyInfo step should be shown
  const selectedClassifications = methods.watch('contactClassifications') || [];

  // Check if any selected classification is "Extended" or "SingleParent" - or, for a supported
  // entered again, one they already hold in this fund
  const isFamilyClassification = (name?: string) => name === 'Extended' || name === 'SingleParent';
  const shouldShowFamilyInfo =
    selectedClassifications.some((cc) => isFamilyClassification(classifications?.find((c) => c.id === cc.classificationId)?.name)) ||
    standingClassificationNames.some(isFamilyClassification);

  // Check if documents section should be shown (only when non-Basic classification is selected)
  const shouldShowDocuments = selectedClassifications.some((cc) => {
    const classification = classifications?.find((c) => c.id === cc.classificationId);
    return classification?.name !== 'Basic';
  });

  // Check if declarations section should be shown (only when classifications are selected)
  const shouldShowDeclarations = selectedClassifications.length > 0;

  // Clear family info data when relevant classifications are removed
  useEffect(() => {
    if (!shouldShowFamilyInfo) {
      // Clear children data and documents
      methods.setValue('children', []);
      methods.setValue('childrenDocuments', []);
      // Uncomment when spouse section is needed:
      // methods.setValue('spouse', null);
    }
  }, [shouldShowFamilyInfo, methods]);

  useEffect(() => {
    if (existingSupported) {
      methods.reset(mapExistingSupportedToFormData(existingSupported));
    } else if (initialValues) {
      /*
       * Merged one level down, not spread flat: the draft carries only the fields that were
       * typed, and a shallow spread of a partial `contact` would drop the defaults for every
       * field the coordinator had not reached yet, leaving controlled inputs undefined.
       *
       * Ahead of initialIsraeliId on purpose - the draft's own id is the one that was typed
       * in front of the supported, and the two agree anyway when the draft was opened by id.
       */
      methods.reset({
        ...defaultFormValues,
        ...initialValues,
        contact: { ...defaultFormValues.contact, ...(initialValues.contact ?? {}) },
        spouse: initialValues.spouse
          ? { ...(defaultFormValues.spouse ?? {}), ...initialValues.spouse }
          : defaultFormValues.spouse,
      });
    } else if (initialIsraeliId) {
      methods.reset({
        ...defaultFormValues,
        contact: {
          ...defaultFormValues.contact,
          israeliId: initialIsraeliId,
        },
      });
    } else {
      methods.reset(defaultFormValues);
    }
  }, [initialIsraeliId, existingSupported, initialValues]);

  /*
   * HAT-1453: "Basic" is the classification nearly every new supported starts with, so it is
   * ticked for the coordinator; the others are an addition to it, not a replacement. Seeded only
   * into an empty selection, and after the reset effect above (effects run in order) so that
   * reset does not wipe it. A draft carries no classifications, so it gets the same start.
   * Ticking it off again stays possible - this runs when the form is (re)initialised, not on
   * every render.
   */
  useEffect(() => {
    const basic = classifications?.find((c) => c.name === 'Basic');
    if (!basic || (methods.getValues('contactClassifications') ?? []).length > 0) return;
    // A supported entered again may hold it already, and cannot be given it twice.
    if (existingClassificationIds.includes(basic.id)) return;
    methods.setValue(
      'contactClassifications',
      [{ id: 0, classificationId: basic.id, requiredDocuments: [], additionalDocuments: [], comments: '' }],
      { shouldDirty: false },
    );
  }, [classifications, initialIsraeliId, existingSupported, initialValues, existingClassificationIds]);

  const formatErrors = (errors: any): string => {
    const errorList: string[] = [];
    const children = methods.getValues('children') || [];

    const extractErrors = (obj: any, path: string = '') => {
      Object.keys(obj).forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;

        if (obj[key]?.message) {
          // Check if this is a child israeliId error - show the actual ID that is invalid
          const childIdMatch = currentPath.match(/^children\.(\d+)\.israeliId$/);
          const childFieldMatch = currentPath.match(/^children\.(\d+)\.\w+$/);
          if (childIdMatch) {
            const childIndex = parseInt(childIdMatch[1], 10);
            const israeliId = children[childIndex]?.israeliId;
            if (israeliId) {
              errorList.push(`${t('israeliId')} ${israeliId} ${t('invalid')}`);
            } else {
              errorList.push(obj[key].message);
            }
          } else if (childFieldMatch) {
            // Prefix other child errors with the child's name so it is clear
            // which child is missing data
            const firstName = children[parseInt(childFieldMatch[1], 10)]?.firstName;
            errorList.push(firstName ? `${firstName}: ${obj[key].message}` : obj[key].message);
          } else {
            errorList.push(obj[key].message);
          }
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          extractErrors(obj[key], currentPath);
        }
      });
    };

    extractErrors(errors);
    return errorList.length > 0 ? errorList.join('\n') : t('pleaseCompleteAllRequiredFields');
  };

  const validateFields = async (fields: FieldPath<SupportedFormData>[]) => {
    const isValid = await methods.trigger(fields);
    if (!isValid) {
      const errorMessage = formatErrors(methods.formState.errors);
      alert(`${t('pleaseCompleteAllRequiredFields')}:\n\n${errorMessage}`);
    }
    return isValid;
  };

  // A kept existing account counts, so a supported entered again may keep theirs and add none.
  const validateBankAccountsStep = async () => {
    const total = (methods.getValues('paymentInfos') ?? []).length + (methods.getValues('existingPaymentInfos') ?? []).length;
    if (total === 0) {
      alert(`${t('pleaseCompleteAllRequiredFields')}:\n\n${MIN_ONE_BANK_ACCOUNT}`);
      return false;
    }
    return validateFields(['paymentInfos']);
  };

  const validateDocumentsStep = async () => {
    const contactClassifications = methods.getValues('contactClassifications') || [];
    const errors: string[] = [];

    contactClassifications.forEach((cc) => {
      const classification = classifications?.find((c) => c.id === cc.classificationId);
      if (classification?.requiredDocuments && classification.requiredDocuments.length > 0) {
        if ((cc.requiredDocuments || []).length === 0) {
          errors.push(`סיווג: ${t(classification.name)}: יש להעלות לפחות מסמך אחד`);
        }
      }
    });

    if (errors.length > 0) {
      methods.setError('contactClassifications', {
        type: 'manual',
        message: errors.join('\n'),
      });
      alert(`${t('pleaseCompleteAllRequiredFields')}:\n\n${errors.join('\n')}`);
      return false;
    }

    return true;
  };

  const validateDeclarationsStep = async () => {
    const declarationsComplete = methods.getValues('declarationsComplete');
    
    if (!declarationsComplete) {
      const errorMessage = t('declarationsNotComplete') || 'יש לסמן את כל ההצהרות ולמלא את שם המצהיר';
      methods.setError('declarationsComplete', {
        type: 'manual',
        message: errorMessage,
      });
      alert(`${t('pleaseCompleteAllRequiredFields')}:\n\n${errorMessage}`);
      return false;
    }

    return true;
  };

  const validateFamilyInfoStep = async () => {
    // First validate the children fields (birth date included - it is required)
    const isFieldsValid = await methods.trigger(['children']);
    if (!isFieldsValid) {
      const errorMessage = formatErrors(methods.formState.errors);
      alert(`${t('pleaseCompleteAllRequiredFields')}:\n\n${errorMessage}`);
      return false;
    }

    // Then check if at least one document is uploaded when there are children
    const children = methods.getValues('children') || [];
    const childrenDocuments = methods.getValues('childrenDocuments') || [];

    if (children.length > 0 && childrenDocuments.length === 0) {
      const errorMessage = t('childrenDocumentsRequired') || 'יש להעלות לפחות מסמך אחד עבור הילדים';
      methods.setError('childrenDocuments', {
        type: 'manual',
        message: errorMessage,
      });
      alert(`${t('pleaseCompleteAllRequiredFields')}:\n\n${errorMessage}`);
      return false;
    }

    return true;
  };

  // Build step definitions dynamically based on selected classifications
  const stepDefinitions: StepDefinition[] = [
    {
      // The stepper lays its content out in a row, so the notice and the form share one column of
      // the form's own width - otherwise the notice stands beside the form.
      content: reenteredSupportedId ? (
        <div className="flex flex-col gap-6 w-[850px] max-md:w-full">
          <Alert type="info" dismissible={false} title={t('reenterSupportedNoticeTitle')} body={t('reenterSupportedNoticeBody')} />
          <SupportedPersonalInfo />
        </div>
      ) : (
        <SupportedPersonalInfo />
      ),
      validate: () => validateFields(['contact']),
    },
    {
      content: <BankAccountInfo />,
      validate: validateBankAccountsStep,
    },
    {
      content: <ClassifictionsSection existingClassificationIds={existingClassificationIds} />,
      validate: () => validateFields(['contactClassifications']),
    },
    // Show DocumentsSection only if a non-Basic classification is selected
    ...(shouldShowDocuments ? [{
      content: <DocumentsSection />,
      validate: validateDocumentsStep,
    }] : []),
    // Show FamilyInfo step only if "Extended" or "SingleParent" classification is selected
    ...(shouldShowFamilyInfo ? [{
      content: <FamilyInfo />,
      validate: validateFamilyInfoStep, // Add 'spouse' validation when spouse section is uncommented
    }] : []),
    // Show DeclarationsSection only if classifications are selected
    ...(shouldShowDeclarations ? [{
      content: <DeclarationsSection />,
      validate: validateDeclarationsStep,
    }] : []),
  ];

  // Creates a supported, or - for a deleted one entered again (HAT-1464) - corrects the deleted
  // record. Existing children and bank accounts are not edited here, only kept or dropped.
  const submitForm = async (data: SupportedFormData) => {
    if (!currentFund?.id) {
      alert(t('errorMissingData') || 'Missing required data');
      return;
    }

    const apiData = reenteredSupportedId
      ? await mapFormDataToReenterDto(data, { includeChildren: shouldShowFamilyInfo })
      : await mapFormDataToDto(data);

    createSupported(apiData, {
      onSuccess: (created: SupportedFullInfoDtoIn) => {
        const hasBasic =
          data.contactClassifications.some((cc) => classifications?.find((c) => c.id === cc.classificationId)?.name === 'Basic') ||
          standingClassificationNames.includes('Basic');
        onSuccess(created.contact?.id ?? null, created.contact ? { contact: created.contact, canRequest: hasBasic } : undefined);
        offerTaskSatisfaction('newSupported');
        methods.reset(defaultFormValues);
      },
      onError: (error: any) => {
        const responseData = error?.response?.data;
        // The re-enter endpoint answers 409 for more than a duplicate account, with an errorCode.
        if (error?.response?.status === 409 && !responseData?.errorCode) {
          onError(t('duplicatePaymentInfoError'));
        } else if (responseData?.errorCode) {
          onError(t(responseData.errorCode));
        } else {
          const errorMessage = responseData || error?.message || t('errorCreatingSupported');
          onError(errorMessage);
        }
      },
    });
  };

  const handleSubmit = () => {
    return methods.handleSubmit(submitForm)();
  };

  return (
    <FormProvider {...methods}>
      <FormStepper steps={stepDefinitions} onSubmit={handleSubmit} isSubmitting={isSubmitting || methods.formState.isSubmitting} />
    </FormProvider>
  );
};

export default NewSupportedForm;

